import { AdminRole } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { ActivityIndicator, Modal, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from '../TournamentDetailScreen.styles';

interface AddAdminModalProps {
  visible: boolean;
  searchEmail: string;
  setSearchEmail: (email: string) => void;
  selectedRole: AdminRole;
  setSelectedRole: (role: AdminRole) => void;
  searching: boolean;
  onAdd: () => void;
  onCancel: () => void;
  colors: {
    overlay: string;
    card: string;
    surface: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
  };
}

export const AddAdminModal = ({
  visible,
  searchEmail,
  setSearchEmail,
  selectedRole,
  setSelectedRole,
  searching,
  onAdd,
  onCancel,
  colors,
}: AddAdminModalProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  return (
    <Modal visible={visible} animationType="slide" transparent={true} onRequestClose={onCancel}>
      <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
        <View style={[styles.addAdminModal, { backgroundColor: colors.card }]}>
          <Text style={[styles.addAdminModalTitle, { color: colors.text }]}>{t('tournaments.addAdministrator')}</Text>

          <Text style={[styles.addAdminModalLabel, { color: colors.textSecondary }]}>
            {t('tournaments.enterEmailForAdmin')}
          </Text>

          <TextInput
            style={[
              styles.addAdminInput,
              { backgroundColor: colors.surface, borderColor: colors.border, color: colors.text },
            ]}
            placeholder="email@example.com"
            placeholderTextColor={colors.textTertiary}
            value={searchEmail}
            onChangeText={setSearchEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            editable={!searching}
          />

          <Text style={[styles.addAdminModalLabel, { color: colors.text, marginTop: 15 }]}>{t('tournaments.selectRole')}</Text>

          <View style={styles.roleSelector}>
            <TouchableOpacity
              style={[
                styles.roleOption,
                {
                  borderColor: colors.border,
                  backgroundColor: selectedRole === 'admin' ? colors.primary : colors.card,
                },
              ]}
              onPress={() => setSelectedRole('admin')}
            >
              <Text
                style={[
                  styles.roleOptionTitle,
                  { color: selectedRole === 'admin' ? colors.primaryText : colors.text },
                ]}
              >
                {t('tournaments.admin')}
              </Text>
              <Text
                style={[
                  styles.roleOptionDesc,
                  { color: selectedRole === 'admin' ? colors.primaryText : colors.textSecondary },
                ]}
              >
                {t('tournaments.adminRoleDesc')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.roleOption,
                {
                  borderColor: colors.border,
                  backgroundColor: selectedRole === 'viewer' ? colors.primary : colors.card,
                },
              ]}
              onPress={() => setSelectedRole('viewer')}
            >
              <Text
                style={[
                  styles.roleOptionTitle,
                  { color: selectedRole === 'viewer' ? colors.primaryText : colors.text },
                ]}
              >
                {t('tournaments.viewer')}
              </Text>
              <Text
                style={[
                  styles.roleOptionDesc,
                  { color: selectedRole === 'viewer' ? colors.primaryText : colors.textSecondary },
                ]}
              >
                {t('tournaments.viewerRoleDesc')}
              </Text>
            </TouchableOpacity>
          </View>

          <TouchableOpacity
            style={[styles.addAdminModalBtn, { backgroundColor: searching ? colors.border : colors.primary }]}
            onPress={onAdd}
            disabled={searching}
          >
            {searching ? (
              <ActivityIndicator color={colors.primaryText} />
            ) : (
              <Text style={[styles.addAdminModalBtnText, { color: colors.primaryText }]}>{t('tournaments.addAdministrator')}</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity style={styles.addAdminModalCancelBtn} onPress={onCancel}>
            <Text style={[styles.addAdminModalCancelBtnText, { color: colors.primary }]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};
